import { supabase } from '@/integrations/supabase/client'
import { Logger } from '@/lib/utils/logger'
import { AuthResponse as SupabaseAuthResponse } from '@supabase/supabase-js';

const logger = new Logger('AuthService')

export type UserRole = 'specialist' | 'caregiver'

interface AuthResponse {
  success: boolean
  session?: any
  error?: string
}

interface UserRoleResponse {
  role: UserRole;
}

interface VerifyUserRoleResponse {
  exists: boolean;
  has_role: boolean;
  debug_role?: string;
}

export class AuthService {
  private static async getUserRole(userId: string): Promise<UserRole | null> {
    logger.info('Fetching user role', { userId })
    
    const { data: { session } } = await supabase.auth.getSession()
    
    if (!session || session.user.id !== userId) {
      logger.error('User not found or not authorized')
      return null
    }
    
    const role = session.user.user_metadata?.role as UserRole
    logger.info('User role fetched successfully', { role })
    return role || null
  }

  static async signIn(email: string, password: string, expectedRole: UserRole): Promise<AuthResponse> {
    logger.info('Starting sign in process', { email, expectedRole })
    
    try {
      // Step 1: Verify if user exists and has the correct role
      const { data } = await supabase.rpc('verify_user_role', { 
        p_email: email,
        p_role: expectedRole
      });

      // First cast to unknown, then to our expected type
      const verifyData = (data as unknown) as VerifyUserRoleResponse;
      
      if (!verifyData.exists) {
        return {
          success: false,
          error: 'No account found with this email. Please sign up first.'
        };
      }

      if (!verifyData.has_role) {
        return {
          success: false,
          error: `This account is not registered as a ${expectedRole}. Please use the correct login page.`
        };
      }

      // Step 2: Try to sign in
      logger.info('Attempting Supabase authentication')
      try {
        logger.info('Making Supabase API request...');
        
        // First sign in the user
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        // Set session persistence after sign-in if it was successful
        if (!error) {
          await supabase.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
          
          // Set local storage flag to remember the user
          localStorage.setItem('rememberSession', 'true');
          
          logger.info('Session persistence enabled');
        }
        
        logger.info('Received response from Supabase');
        
        if (error) {
          logger.error('Authentication failed', { error });
          return {
            success: false,
            error: 'Invalid email or password. Please try again.'
          };
        }

        if (!data?.user?.id) {
          logger.error('No user ID in auth response')
          return {
            success: false,
            error: 'Authentication failed: No user ID received'
          }
        }

        // Then update the user metadata with the role
        const { error: updateError } = await supabase.auth.updateUser({
          data: { role: expectedRole }
        });

        if (updateError) {
          logger.error('Failed to update user role', { error: updateError });
          return {
            success: false,
            error: 'Failed to set user role'
          };
        }

        logger.info('Sign in successful', { userId: data.user.id, role: expectedRole })
        return {
          success: true,
          session: data.session
        }
      } catch (signInError) {
        logger.error('Supabase sign in error', { error: signInError });
        return {
          success: false,
          error: 'Unable to connect to authentication service. Please try again later.'
        };
      }
    } catch (error) {
      logger.error('Unexpected error during sign in', { error })
      return {
        success: false,
        error: 'An unexpected error occurred. Please try again later.'
      }
    }
  }

  static async signOut(): Promise<void> {
    logger.info('Signing out user')
    await supabase.auth.signOut()
    logger.info('Sign out completed')
  }

  static async getCurrentSession() {
    logger.info('Getting current session')
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (error) {
      logger.error('Error getting session', { error })
      return null
    }
    
    logger.info('Session retrieved successfully', { hasSession: !!session })
    return session
  }

  /**
   * Sends a password reset email to the specified email address
   * @param email The email address to send the reset link to
   * @param role The user role (caregiver or specialist)
   * @returns An object indicating success or failure with an optional error message
   */
  static async resetPassword(email: string, role: UserRole = 'caregiver'): Promise<AuthResponse> {
    logger.info('Initiating password reset', { email, role })
    
    try {
      // First check if the user exists and has the correct role
      const { data } = await supabase.rpc('verify_user_role', { 
        p_email: email,
        p_role: role
      });

      // First cast to unknown, then to our expected type
      const verifyData = (data as unknown) as VerifyUserRoleResponse;
      
      if (!verifyData.exists) {
        logger.warn('Password reset attempted for non-existent account', { email })
        return {
          success: false,
          error: 'No account found with this email.'
        };
      }

      if (!verifyData.has_role) {
        logger.warn(`Password reset attempted for non-${role} account`, { email })
        return {
          success: false,
          error: `This account is not registered as a ${role}.`
        };
      }

      // Determine the redirect URL based on the role
      const redirectPath = role === 'specialist' ? '/coach-reset-password' : '/reset-password';

      // Send the password reset email
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}${redirectPath}`,
      })
      
      if (error) {
        logger.error('Failed to send password reset email', { error })
        return {
          success: false,
          error: 'Failed to send password reset email. Please try again later.'
        }
      }
      
      logger.info('Password reset email sent successfully', { email })
      return {
        success: true
      }
    } catch (error) {
      logger.error('Unexpected error during password reset', { error })
      return {
        success: false,
        error: 'An unexpected error occurred. Please try again later.'
      }
    }
  }
}
